import React, { useState, useEffect } from 'react';
import { Card, Row, Col, DatePicker, Statistic, Table } from 'antd';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { paymentService, PAYMENT_TYPES } from '../../services/paymentService';
import { studentService } from '../../services/studentService';
import dayjs from 'dayjs';
import AV from 'leancloud-storage';
import { message } from 'antd';

const { RangePicker } = DatePicker;

const FinancialStats = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalExpense: 0,
    totalRefund: 0,
    monthlyStats: [],
    paymentMethods: [],
    recentTransactions: [],
    verifiedStudentsIncome: 0,
    studentCountInRange: 0
  });

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [startDate, endDate] = dateRange;
      
      // Fetch all payments within date range
      const payments = await paymentService.getPayments({
        startDate: startDate.toDate(),
        endDate: endDate.toDate()
      });

      // Calculate payment totals
      const paymentIncome = payments
        .filter(p => p.type === PAYMENT_TYPES.TUITION)
        .reduce((sum, p) => sum + p.amount, 0);

      const totalExpense = payments
        .filter(p => p.type === PAYMENT_TYPES.EXPENSE)
        .reduce((sum, p) => sum + p.amount, 0);

      const totalRefund = payments
        .filter(p => p.type === PAYMENT_TYPES.REFUND)
        .reduce((sum, p) => sum + p.amount, 0);

      // Fetch verified students within date range
      const query = new AV.Query('Student');
      query.equalTo('verified', true); // Only verified students
      query.greaterThanOrEqualTo('signUpDate', startDate.format('YYYY-MM-DD'));
      query.lessThanOrEqualTo('signUpDate', endDate.format('YYYY-MM-DD'));
      
      const verifiedStudents = await query.find();
      console.log(`Found ${verifiedStudents.length} verified students in date range`);
      
      // Calculate tuition fee income from verified students
      const verifiedStudentsIncome = verifiedStudents.reduce((sum, student) => {
        const tuitionFee = student.get('tuitionFee') || 0;
        return sum + tuitionFee;
      }, 0);
      
      console.log('Verified students income:', verifiedStudentsIncome);
      
      // Total income is the sum from verified students
      const totalIncome = verifiedStudentsIncome;

      // Group by month for trend analysis (monthly data now based on student sign up date)
      const monthlyData = {};
      
      // First add payment data
      payments.forEach(payment => {
        const month = dayjs(payment.date).format('YYYY-MM');
        if (!monthlyData[month]) {
          monthlyData[month] = { 
            month,
            income: 0,
            expense: 0,
            refund: 0
          };
        }
        
        if (payment.type === PAYMENT_TYPES.EXPENSE) {
          monthlyData[month].expense += payment.amount;
        } else if (payment.type === PAYMENT_TYPES.REFUND) {
          monthlyData[month].refund += payment.amount;
        }
      });
      
      // Then add verified student tuition fees to monthly income
      verifiedStudents.forEach(student => {
        const signUpDate = student.get('signUpDate');
        if (signUpDate) {
          const month = dayjs(signUpDate).format('YYYY-MM');
          if (!monthlyData[month]) {
            monthlyData[month] = { 
              month,
              income: 0,
              expense: 0,
              refund: 0
            };
          }
          
          monthlyData[month].income += student.get('tuitionFee') || 0;
        }
      });

      // Payment method statistics (keep as is for now)
      const methodStats = {};
      payments.forEach(payment => {
        if (!methodStats[payment.method]) {
          methodStats[payment.method] = { 
            name: payment.method, 
            value: 0,
            count: 0 
          };
        }
        methodStats[payment.method].value += payment.amount;
        methodStats[payment.method].count += 1;
      });

      setStats({
        totalIncome,
        totalExpense,
        totalRefund,
        monthlyStats: Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month)),
        paymentMethods: Object.values(methodStats),
        recentTransactions: payments.slice(0, 10), // Latest 10 transactions
        verifiedStudentsIncome,
        studentCountInRange: verifiedStudents.length
      });

    } catch (error) {
      console.error('Error fetching financial stats:', error);
      message.error('获取财务统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const renderOverview = () => (
    <Row gutter={16}>
      <Col span={8}>
        <Card>
          <Statistic
            title="总收入 (已审核学员学费)"
            value={stats.totalIncome}
            precision={2}
            prefix="¥"
            loading={loading}
          />
          <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
            期间内审核学员: {stats.studentCountInRange} 人
          </div>
        </Card>
      </Col>
      <Col span={8}>
        <Card>
          <Statistic
            title="总支出"
            value={stats.totalExpense}
            precision={2}
            prefix="¥"
            loading={loading}
          />
        </Card>
      </Col>
      <Col span={8}>
        <Card>
          <Statistic
            title="总退款"
            value={stats.totalRefund}
            precision={2}
            prefix="¥"
            loading={loading}
          />
        </Card>
      </Col>
    </Row>
  );

  const renderTrend = () => (
    <Card title="月度趋势" style={{ marginTop: 16 }}>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={stats.monthlyStats}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="income" name="收入 (已审核学费)" stroke="#52c41a" />
          <Line type="monotone" dataKey="expense" name="支出" stroke="#f5222d" />
          <Line type="monotone" dataKey="refund" name="退款" stroke="#faad14" />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );

  const renderPaymentMethods = () => (
    <Card title="支付方式统计" style={{ marginTop: 16 }}>
      <Row gutter={16}>
        <Col span={12}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stats.paymentMethods}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                label
              />
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Col>
        <Col span={12}>
          <Table
            size="small"
            pagination={false}
            dataSource={stats.paymentMethods}
            columns={[
              { title: '支付方式', dataIndex: 'name' },
              { title: '金额', dataIndex: 'value', render: val => `¥${val.toFixed(2)}` },
              { title: '笔数', dataIndex: 'count' }
            ]}
          />
        </Col>
      </Row>
    </Card>
  );

  const renderRecentTransactions = () => (
    <Card title="最近交易" style={{ marginTop: 16 }}>
      <Table
        size="small"
        dataSource={stats.recentTransactions}
        columns={[
          {
            title: '日期',
            dataIndex: 'date',
            key: 'date',
            render: date => dayjs(date).format('YYYY-MM-DD')
          },
          {
            title: '类型',
            dataIndex: 'type',
            key: 'type',
            render: type => ({
              [PAYMENT_TYPES.TUITION]: '学费',
              [PAYMENT_TYPES.EXPENSE]: '支出',
              [PAYMENT_TYPES.REFUND]: '退款'
            }[type])
          },
          {
            title: '金额',
            dataIndex: 'amount',
            key: 'amount',
            render: amount => `¥${amount.toFixed(2)}`
          },
          {
            title: '支付方式',
            dataIndex: 'method',
            key: 'method'
          },
          {
            title: '说明',
            dataIndex: 'description',
            key: 'description'
          },
          {
            title: '操作人',
            dataIndex: 'createdBy',
            key: 'createdBy'
          }
        ]}
        pagination={false}
      />
    </Card>
  );

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          style={{ marginBottom: 16 }}
        />
        {renderOverview()}
      </Card>
      {renderTrend()}
      {renderPaymentMethods()}
      {renderRecentTransactions()}
    </div>
  );
};

export default FinancialStats; 